import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';
import { NotificationChannelSchema } from '../notification-channel/notification-channel';
import { NotificationTemplateKeySchema } from '../notification-template-key/notification-template-key';

/**
 * Where one delivery stands (N17).
 *
 * `sending` is not bookkeeping: it is the **claim** N27 requires before an
 * irreversible act. A push that has left the process cannot be recalled, so the
 * worker moves the row into `sending` in one guarded statement — which is also
 * what stops two workers sending the same notice twice — and reverts it if the
 * send throws.
 *
 * `dead` is terminal and deliberately visible. A notice nobody received is
 * evidence, not garbage; the admin dead-letter view is where an operator finds
 * out that FCM has been refusing their service account for two days.
 */
export const NOTIFICATION_DELIVERY_STATUSES = [
  'queued',
  'sending',
  'sent',
  'failed',
  'dead',
] as const;

export const NotificationDeliveryStatusSchema = z.enum(NOTIFICATION_DELIVERY_STATUSES);

export type NotificationDeliveryStatus = (typeof NOTIFICATION_DELIVERY_STATUSES)[number];

/** A delivery that has stopped moving on its own and can be retried by hand. */
export function isRetriableDeliveryStatus(status: NotificationDeliveryStatus): boolean {
  return status === 'dead' || status === 'failed';
}

/**
 * Why a delivery failed, in a form the admin log can group by.
 *
 * A free-text `lastError` alone would make "is this one broken template or a
 * broken integration?" a reading exercise across hundreds of rows. The reason is
 * a **coded** fact and the vendor's words ride beside it.
 */
export const NOTIFICATION_FAILURE_REASONS = [
  'template_missing',
  'template_inactive',
  'template_render_failed',
  'no_device_token',
  'push_rejected',
  'push_unavailable',
  // The email channel's three (P21e1), appended last for the pg enum's sake:
  // nothing to send with, the recipient refused by the server, the server gone.
  'email_unconfigured',
  'email_rejected',
  'email_unavailable',
  // The sms channel's three (P30b1), the same split over the vendor.
  'sms_unconfigured',
  'sms_rejected',
  'sms_unavailable',
] as const;

export const NotificationFailureReasonSchema = z.enum(NOTIFICATION_FAILURE_REASONS);

export type NotificationFailureReason = (typeof NOTIFICATION_FAILURE_REASONS)[number];

export const NotificationDeliverySchema = z
  .object({
    id: UuidSchema,
    notificationId: UuidSchema.nullable(),
    /**
     * The campaign this delivery belongs to, or null for an ordinary notice.
     *
     * Beside `notificationId` rather than instead of it: a campaign delivery has
     * both — the notification row it rendered, and the walk that asked for it.
     * It is what makes "how did that announcement actually go?" one filtered
     * query on the log the operator already knows, instead of a second table.
     */
    campaignId: UuidSchema.nullable(),
    userId: UuidSchema,
    templateKey: NotificationTemplateKeySchema,
    channel: NotificationChannelSchema,
    /**
     * The words this channel carried — null on a delivery that never rendered.
     *
     * On the delivery rather than only on the notification because a push and an
     * in-app row are two different sentences, and because a push whose in-app
     * half a preference suppressed has no notification row behind it. It is also
     * the answer to the one question a support role opens this log to ask: what
     * exactly did we send them?
     */
    title: z.string().nullable(),
    body: z.string().nullable(),
    status: NotificationDeliveryStatusSchema,
    attempts: z.number().int().min(0),
    failureReason: NotificationFailureReasonSchema.nullable(),
    /** The vendor's or the renderer's own words, truncated. Never a credential. */
    lastError: z.string().nullable(),
    /**
     * The vendor's own identifier for what it accepted — an SMTP `Message-ID`,
     * a verification SID — or null where the channel has none (push, in-app) or
     * nothing was accepted. What a support role quotes to the vendor.
     */
    providerMessageId: z.string().nullable(),
    sentAt: z.iso.datetime().nullable(),
    deadLetteredAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
  })
  .strict();

export type NotificationDelivery = z.infer<typeof NotificationDeliverySchema>;
